import {
  prisma,
  type Prisma,
  ResourceType,
  ActionType,
  ContactBookGroupType,
  InvitationAttendeeState,
} from "client";
import { getDoorManagementListByEvent } from "services/profile";

export const getInvitationAttendeeById = async (id: string) => {
  try {
    const invitationAttendee = await prisma.invitationAttendee.findFirst({
      where: {
        id: id,
      },
      include: {
        contact: true,
      },
    });

    return invitationAttendee;
  } catch (e) {
    return { error: e };
  }
};
export const getInvitationAttendeeByInvitationId = async (
  // userId: string,
  invitationId: string
) => {
  try {
    const invitationAttendees = await prisma.invitationAttendee.findMany({
      where: {
        invitationId: invitationId,
      },
      include: {
        contact: true,
      },
    });

    return invitationAttendees;
  } catch (e) {
    return { error: e };
  }
};

// POST
export const createInvitationAttendee = async (
  userId: string,
  invitationId: string,
  invitationAttendeeId: string,
  attendeeCategoryId: string
) => {
  try {
    const invitationAttendee = await prisma.invitationAttendee.create({
      data: {
        contact: {
          connect: { id: invitationAttendeeId },
        },
        invitation: {
          connect: {
            id: invitationId,
          },
        },
        attendeeCategory: {
          connect: { id: attendeeCategoryId }, // Add attendeeCategory connection
        },
      },
    });
    return invitationAttendee;
  } catch (e) {
    console.error(e);
    return { error: e };
  }
};

export const findOrCreateAttendee = async (
  invitationId: string,
  contactId: string,
  attendeesCategoryId: string
) => {
  try {
    return await prisma.invitationAttendee.upsert({
      where: {
        attendeesCategoryId_contactId_invitationId: {
          attendeesCategoryId,
          contactId,
          invitationId,
        },
      },
      create: { contactId, attendeesCategoryId, invitationId },
      update: {}, // find-or-create: don't change existing row
      include: { contact: true },
    });
  } catch (e) {
    console.error(e);
    return { error: e };
  }
};

export const deleteInvitationAttendee = async (userId: string, id: string) => {
  try {
    const invitationAttendee = await prisma.invitationAttendee.delete({
      where: { id },
    });
    return invitationAttendee;
  } catch (e) {
    return { error: e };
  }
};

// GET
export const getInvitationAttendeeCategory = async (id: string) => {
  try {
    const invitationAttendeeCategory =
      await prisma.invitationAttendeeCategory.findMany({
        where: { invitationId: id },
        include: {
          invitationAttendees: {
            include: {
              contact: true,
              transitions: true,
            },
          },
          contactBookGroup: {
            include: {
              contacts: true,
              _count: {
                select: {
                  contacts: true,
                },
              },
            },
          },
          _count: {
            select: {
              invitationAttendees: true,
            },
          },
        },
        orderBy: {
          id: "desc",
        },
      });

    // Get all category IDs
    const categoryIds = invitationAttendeeCategory.map((cat) => cat.id);

    // Get grouped counts for all categories in one query
    const responseCounts = await prisma.invitationAttendee.groupBy({
      by: ["attendeesCategoryId", "state"],
      where: {
        attendeesCategoryId: {
          in: categoryIds,
        },
      },
      _count: true,
    });

    // Map counts back to categories
    const categoriesWithCounts = invitationAttendeeCategory.map((category) => {
      const counts = responseCounts
        .filter((rc) => rc.attendeesCategoryId === category.id)
        .reduce((acc, item) => {
          acc[item.state] = item._count;
          return acc;
        }, {} as Record<InvitationAttendeeState, number>);

      return {
        ...category,
        responseCounts: counts,
      };
    });

    return categoriesWithCounts;
  } catch (e) {
    console.error(e);
  }
};

// POST
export const createInvitationAttendeeCategory = async (
  invitationId: string,
  contactGroupPayload: Prisma.ContactBookGroupCreateInput,
  userId: string,
  contactGroupId?: string
) => {
  try {
    const invitationAttendeeCategory =
      await prisma.invitationAttendeeCategory.create({
        data: {
          invitation: {
            connect: { id: invitationId },
          },
          createdBy: {
            connect: { id: userId },
          },
          contactBookGroup: contactGroupId
            ? {
                connectOrCreate: {
                  where: { id: contactGroupId }, // Ensure contactGroupId is a valid unique ID
                  create: { ...contactGroupPayload },
                },
              }
            : {
                create: { ...contactGroupPayload }, // If no ID, just create a new group
              },
        },
      });
    return invitationAttendeeCategory;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

//UPDATE
export const updateInvitationAttendeeCategory = async (
  id: string,
  data: Prisma.InvitationAttendeeCategoryUpdateInput,
  userId: string
) => {
  try {
    const invitationAttendeeCategory =
      await prisma.invitationAttendeeCategory.update({
        where: { id },
        data: {
          ...data,
          updatedById: userId,
        },
      });
    return invitationAttendeeCategory;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

//DELETE
export const deleteInvitationAttendeeCategory = async (id: string) => {
  try {
    const invitationAttendeeCategory =
      await prisma.invitationAttendeeCategory.delete({
        where: { id },
      });
    return invitationAttendeeCategory;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const createInvitationAttendeeWithContact = async (
  invitationId: string,
  contactPayload: Prisma.ProfileCreateInput,
  listType: string,
  eventId: string,
  attendeeId?: string
) => {
  try {
    let listId;
    if (listType === ContactBookGroupType.DOOR_MANAGEMENT) {
      const list = await getDoorManagementListByEvent(eventId);
      if (list?.id) listId = list.id;
    }
    if (!listId) {
      const contactBookGroup = await prisma.contactBookGroup.create({
        data: {
          name: listType,
          event: { connect: { id: eventId } },
        },
      });
      listId = contactBookGroup.id;
    }
    const contact = await prisma.profile.create({
      data: {
        ...contactPayload,
        contactBookGroup: { connect: { id: listId } },
        event: { connect: { id: eventId } },
      },
    });

    const attendeeCategory = await prisma.invitationAttendeeCategory.upsert({
      where: {
        contactBookGroupId_invitationId: {
          contactBookGroupId: listId,
          invitationId: invitationId,
        },
      },
      update: {},
      create: {
        invitationId,
        contactBookGroupId: listId,
      },
    });
    let invitationAttendee;
    if (attendeeId) {
      invitationAttendee = await prisma.invitationAttendee.update({
        where: { id: attendeeId },
        data: {
          attendeesCategoryId: attendeeCategory.id,
          invitationId,
          contactId: contact.id,
        },
      });
    } else
      invitationAttendee = await prisma.invitationAttendee.create({
        data: {
          attendeesCategoryId: attendeeCategory.id,
          invitationId,
          contactId: contact.id,
        },
      });

    return invitationAttendee;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
